import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import type { DashboardPeriod } from './finance.api'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_DASHBOARD')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export interface CaseAcceptanceQueryParams {
  clinicId: string
  date: string
  period: DashboardPeriod
}

export interface CaseAcceptanceOverviewKpiData {
  acceptanceRate: string
  acceptanceGoal: string
  primaryValue: number
  secondaryValue: number
  acceptedVal: string
  sameDayVal: string
  pendingVal: string
  caseAcceptanceRate: string
}

export interface CaseAcceptanceOverviewResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  goals: null
  kpis: CaseAcceptanceOverviewKpiData
}

export interface CaseAcceptanceKpiData {
  acceptanceRate: string
  acceptanceGoal: string
  casesPresented: number
  casesAccepted: number
  acceptedValue: string
  sameDayValue: string
  pendingValue: string
  comparisonLabel: string
  rateDelta: string
  rateTrend: 'up' | 'down'
  presentedDelta: string
  presentedTrend: 'up' | 'down'
  acceptedValueDelta: string
  acceptedValueTrend: 'up' | 'down'
  sameDayDelta: string
  sameDayTrend: 'up' | 'down'
  pendingDelta: string
  pendingTrend: 'up' | 'down'
}

export interface CaseAcceptanceChartPoint {
  label: string
  scheduled: number
  opened: number
  isCurrent?: boolean
}

export interface CaseAcceptanceChartConfig {
  data: CaseAcceptanceChartPoint[]
  yAxisDomain: [number, number]
  yAxisTicks: number[]
  axisUnit: 'count'
}

export interface CaseAcceptanceByProviderRow {
  id: string
  provider: string
  presented: number
  accepted: number
  acceptanceRate: string
  acceptedValue: string
  sameDayValue: string
}

export interface CaseAcceptanceByCategoryRow {
  id: string
  category: string
  presented: number
  accepted: number
  acceptanceRate: string
  totalValue: string
  avgValue: string
}

export interface CaseAcceptanceByTimePeriodRow {
  id: string
  timePeriod: string
  presented: number
  accepted: number
  acceptanceRate: string
  acceptedValue: string
  pendingValue: string
}

export interface CaseAcceptanceDetailResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  kpis: CaseAcceptanceKpiData
  chart: CaseAcceptanceChartConfig
  byProvider: CaseAcceptanceByProviderRow[]
  byCategory: CaseAcceptanceByCategoryRow[]
  byTimePeriod: CaseAcceptanceByTimePeriodRow[]
}

const caseAcceptanceGet = <T>(
  path: string,
  params: CaseAcceptanceQueryParams,
  options?: { signal?: AbortSignal },
): Promise<T> =>
  axios
    .get<
      ApiResponse<T>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/treatment/case-acceptance/${path}`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getCaseAcceptanceOverview = (
  params: CaseAcceptanceQueryParams,
  options?: { signal?: AbortSignal },
): Promise<CaseAcceptanceOverviewResult> =>
  caseAcceptanceGet('overview', params, options)

export const getCaseAcceptanceDetail = (
  params: CaseAcceptanceQueryParams,
  options?: { signal?: AbortSignal },
): Promise<CaseAcceptanceDetailResult> =>
  caseAcceptanceGet('detail', params, options)
